/** Help Center: module guides and the API guide, searchable (per boss's request). */
import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BookOpen, Check, Code2, Copy, Rocket, Search } from 'lucide-react';
import { ARTICLES, type HelpArticle, type HelpCategory } from './content';

const CATEGORIES: { key: HelpCategory; Icon: typeof BookOpen }[] = [
  { key: 'Getting started', Icon: Rocket },
  { key: 'Modules', Icon: BookOpen },
  { key: 'API guide', Icon: Code2 },
];

/** The article that explains a screen: the longest matching route prefix. */
export function articleFor(pathname: string): string {
  let best: { id: string; len: number } = { id: 'welcome', len: 0 };
  for (const a of ARTICLES) {
    for (const r of a.routes ?? []) {
      const hit = r === '/' ? pathname === '/' : pathname === r || pathname.startsWith(`${r}/`);
      if (hit && r.length > best.len) best = { id: a.id, len: r.length };
    }
  }
  return best.id;
}

const plain = (a: HelpArticle) => `${a.title} ${a.summary} ${a.keywords ?? ''} ${a.sections.map((s) => `${s.heading} ${s.body}`).join(' ')}`.toLowerCase();

function search(q: string): { article: HelpArticle; score: number; snippet: string }[] {
  const terms = q.toLowerCase().split(/\s+/).filter((t) => t.length > 1);
  if (!terms.length) return [];
  return ARTICLES.map((a) => {
    const text = plain(a);
    let score = 0;
    for (const t of terms) {
      if (!text.includes(t)) return { article: a, score: 0, snippet: '' };
      if (a.title.toLowerCase().includes(t)) score += 6;
      if ((a.keywords ?? '').toLowerCase().includes(t)) score += 3;
      score += Math.min(5, text.split(t).length - 1);
    }
    const body = a.sections.map((s) => s.body).join(' ').replace(/[`*[\]()#]/g, '');
    const at = body.toLowerCase().indexOf(terms[0]);
    const snippet = at >= 0 ? `${at > 40 ? '…' : ''}${body.slice(Math.max(0, at - 40), at + 120)}…` : a.summary;
    return { article: a, score, snippet };
  })
    .filter((r) => r.score > 0)
    .sort((x, y) => y.score - x.score);
}

function Code({ text }: { text: string }): JSX.Element {
  const [done, setDone] = useState(false);
  return (
    <div className="group relative my-3">
      <pre className="overflow-x-auto rounded-xl bg-slate-900 p-4 font-mono text-xs leading-relaxed text-slate-100 dark:bg-slate-950">{text}</pre>
      <button
        type="button"
        className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-lg bg-white/10 px-2 py-1 text-xs text-white opacity-80 hover:bg-white/20 group-hover:opacity-100"
        onClick={() => void navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); })}
        aria-label="Copy code"
      >
        {done ? <Check aria-hidden="true" className="h-3.5 w-3.5" /> : <Copy aria-hidden="true" className="h-3.5 w-3.5" />}
        {done ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/** Inline markup: `code`, **bold**, [label](/path). */
function Inline({ text }: { text: string }): JSX.Element {
  const parts: ReactNode[] = [];
  const re = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('`')) parts.push(<code key={k++} className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em] text-slate-800 dark:bg-slate-800 dark:text-slate-100">{tok.slice(1, -1)}</code>);
    else if (tok.startsWith('**')) parts.push(<strong key={k++} className="font-semibold text-slate-900 dark:text-white">{tok.slice(2, -2)}</strong>);
    else {
      const [, label, href] = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok)!;
      const external = /^https?:/.test(href) || href.startsWith('/api/') || href.startsWith('/sim/');
      parts.push(external ? <a key={k++} href={href} target="_blank" rel="noreferrer" className="font-medium text-brand-700 underline decoration-brand/30 underline-offset-2 hover:decoration-brand dark:text-brand-100">{label}</a> : <Link key={k++} to={href} className="font-medium text-brand-700 underline decoration-brand/30 underline-offset-2 hover:decoration-brand dark:text-brand-100">{label}</Link>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function Body({ text }: { text: string }): JSX.Element {
  const blocks: ReactNode[] = [];
  const chunks = text.split(/```/);
  chunks.forEach((chunk, i) => {
    if (i % 2 === 1) {
      blocks.push(<Code key={`c${i}`} text={chunk.replace(/^\n/, '').replace(/\n$/, '')} />);
      return;
    }
    for (const [j, para] of chunk.split(/\n\s*\n/).entries()) {
      const lines = para.split('\n').filter((l) => l.trim());
      if (!lines.length) continue;
      const key = `p${i}-${j}`;
      if (lines.every((l) => /^- /.test(l))) blocks.push(<ul key={key} className="my-2 list-disc space-y-1.5 pl-5">{lines.map((l, n) => <li key={n}><Inline text={l.slice(2)} /></li>)}</ul>);
      else if (lines.every((l) => /^\d+\. /.test(l))) blocks.push(<ol key={key} className="my-2 list-decimal space-y-1.5 pl-5">{lines.map((l, n) => <li key={n}><Inline text={l.replace(/^\d+\. /, '')} /></li>)}</ol>);
      else blocks.push(<p key={key} className="my-2">{lines.map((l, n) => <Fragment key={n}>{n > 0 && <br />}<Inline text={l.replace(/^- /, '• ')} /></Fragment>)}</p>);
    }
  });
  return <>{blocks}</>;
}

export function HelpPage(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const results = useMemo(() => search(q), [q]);
  const article = ARTICLES.find((a) => a.id === id) ?? ARTICLES[0];
  const idx = ARTICLES.indexOf(article);
  return (
    <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
      <aside className={`space-y-4 lg:sticky lg:top-4 lg:order-none lg:self-start ${id ? 'order-2' : ''}`}>
        <div className="relative">
          <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            type="search"
            className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 dark:border-slate-700 dark:bg-slate-900"
            placeholder="Search help (e.g. 2307, reconcile, webhook)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && results[0]) { navigate(`/help/${results[0].article.id}`); setQ(''); } }}
            aria-label="Search help"
            autoFocus
          />
        </div>
        {q.trim() ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-2 dark:border-slate-800 dark:bg-slate-900" role="listbox" aria-label="Search results">
            {results.length === 0 ? <p className="p-3 text-sm text-slate-500">Nothing found for “{q}”. Try another word.</p> : results.slice(0, 12).map((r) => (
              <Link key={r.article.id} to={`/help/${r.article.id}`} onClick={() => setQ('')} className="block rounded-xl px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{r.article.title}</p>
                <p className="text-xs text-slate-500">{r.article.category}</p>
                <p className="mt-0.5 line-clamp-2 text-xs text-slate-600 dark:text-slate-300">{r.snippet}</p>
              </Link>
            ))}
          </div>
        ) : (
          <nav className="space-y-4" aria-label="Help topics">
            {CATEGORIES.map(({ key, Icon }) => (
              <div key={key}>
                <p className="mb-1 flex items-center gap-2 px-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><Icon aria-hidden="true" className="h-3.5 w-3.5" />{key}</p>
                <ul className="space-y-0.5">
                  {ARTICLES.filter((a) => a.category === key).map((a) => (
                    <li key={a.id}>
                      <Link to={`/help/${a.id}`} aria-current={a.id === article.id ? 'page' : undefined} className={`block rounded-lg px-3 py-1.5 text-sm ${a.id === article.id ? 'bg-brand-50 font-semibold text-brand-700 dark:bg-slate-800 dark:text-brand-100' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>{a.title}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        )}
      </aside>

      <article className="order-1 min-w-0 rounded-2xl lg:order-none border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:p-8">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-100">{article.category}</p>
        <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{article.title}</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-300">{article.summary}</p>
        <div className="mt-6 space-y-6 text-[15px] leading-relaxed text-slate-700 dark:text-slate-300">
          {article.sections.map((s) => (
            <section key={s.heading}>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{s.heading}</h2>
              <Body text={s.body} />
            </section>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap justify-between gap-3 border-t border-slate-200 pt-4 text-sm dark:border-slate-800">
          {idx > 0 ? <Link className="font-medium text-brand-700 hover:underline dark:text-brand-100" to={`/help/${ARTICLES[idx - 1].id}`}>← {ARTICLES[idx - 1].title}</Link> : <span />}
          {idx < ARTICLES.length - 1 && <Link className="font-medium text-brand-700 hover:underline dark:text-brand-100" to={`/help/${ARTICLES[idx + 1].id}`}>{ARTICLES[idx + 1].title} →</Link>}
        </div>
      </article>
    </div>
  );
}
